export const darkenColor = (hexColor: string = "", amount: number = 20): string => {
  // Remove '#' if present
  hexColor = hexColor.replace("#", "");

  // Extract RGB values
  const red = parseInt(hexColor.substring(0, 2), 16);
  const green = parseInt(hexColor.substring(2, 4), 16);
  const blue = parseInt(hexColor.substring(4, 6), 16);

  // Darken the color by the specified amount
  const darken = (value: number) => Math.max(0, value - amount);

  const darkenedRed = darken(red);
  const darkenedGreen = darken(green);
  const darkenedBlue = darken(blue);

  return `#${rgbToHex(darkenedRed)}${rgbToHex(darkenedGreen)}${rgbToHex(darkenedBlue)}`;
};

export const getColor = (): string => {
  const getRandomValue = (min: number, max: number): number =>
    Math.floor(Math.random() * (max - min + 1) + min);

  const red = getRandomValue(0, 127);
  const green = getRandomValue(0, 127);
  const blue = getRandomValue(0, 127);

  const rgbToHex = (rgb: number): string => {
    const hex = rgb.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  const hexRed = rgbToHex(red);
  const hexGreen = rgbToHex(green);
  const hexBlue = rgbToHex(blue);

  return `#${hexRed}${hexGreen}${hexBlue}`;
};

export const lightenColor = (hexColor: string = "", amount: number = 185): string => {
  // Remove '#' if present
  hexColor = hexColor.replace("#", "");

  // Validate hex color length
  if (hexColor.length !== 6) {
    throw new Error("Invalid hex color format");
  }

  // Extract RGB values
  const red: number = parseInt(hexColor.substring(0, 2), 16);
  const green: number = parseInt(hexColor.substring(2, 4), 16);
  const blue: number = parseInt(hexColor.substring(4, 6), 16);

  // Lighten the color by the specified amount
  const lighten = (value: number): number => Math.min(255, value + amount);

  const lightenedRed: number = lighten(red);
  const lightenedGreen: number = lighten(green);
  const lightenedBlue: number = lighten(blue);

  return `#${rgbToHex(lightenedRed)}${rgbToHex(lightenedGreen)}${rgbToHex(lightenedBlue)}`;
};

export const setColorAlpha = (color: string = "", alpha: number): string => {
  // Ensure alpha is within 0-1 range
  alpha = Math.min(1, Math.max(0, alpha));

  // Check if color is in HEX format
  if (color.startsWith("#")) {
    return hexToRgba(color, alpha);
  }

  // Check if color is in RGB(A) format
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?\)/);
  if (rgbaMatch) {
    const [, r, g, b] = rgbaMatch.map(Number);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // Return original color if format is not recognized
  return color;
};

// Helper function to convert RGB to HEX
const rgbToHex = (rgb: number) => {
  const hex = rgb.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
};

// Helper function to convert HEX to RGBA
const hexToRgba = (hex: string, alpha: number): string => {
  let r = 0,
    g = 0,
    b = 0;

  // Handle 3 or 6 character hex
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.substr(1, 2), 16);
    g = parseInt(hex.substr(3, 2), 16);
    b = parseInt(hex.substr(5, 2), 16);
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
